import { createDefaultManageModulesList } from 'components/navigation/navigationUtils';
import { isOwnerOfAccount } from 'actions/roles';
import queryString from 'query-string';
import { getQueryParamsFromLocation, setQueryParamsFromObject } from 'utils/urlHelpers';
import { uniq, valuesIn } from 'lodash-es';
import { BASE_JWT_URL } from 'constants/environmentVariables';
import photoIcon from 'assets/icons/photoIcon.png';
import { dateTimeLogicFormat2, unixToUTCConvertor } from 'utils/timeHelpers';
import axiosInstance from 'services/axiosConfig';
import center from '@turf/center';
import booleanContains from '@turf/boolean-contains';
import { featureCollection, point, points } from '@turf/helpers';

/**
 * remove value from Array if it exist and return Array without value
 * @function removeFromArray
 * @param {array}
 * @param {value}
 * @returns {Boolean}
 */
export const removeFromArray = (array, value) => {
	return array.filter(function (element) {
		return element !== value;
	});
};

/**
 * check if Array Length has properties
 * @function arrayHasProps
 * @param {array}
 * @returns {Boolean}
 */
export const arrayHasProps = array => {
	return array === undefined || array.length === 0 ? false : true;
};

/**
 * check if Object Length has properties
 * @function objectHasKeys
 * @param {object}
 * @returns {Boolean}
 */
export const objectHasKeys = object => {
	if (!object) {
		return false;
	}
	return Object.keys(object).length > 0;
};

/**
 * return String with Capitalize first latter
 * @function capitalizeEachWord
 * @param {string}
 * @returns {string}
 */
export const capitalizeEachWord = string_ => {
	if (!string_) return '';
	string_ = string_.split(' ').map(item => item.charAt(0).toUpperCase() + item.slice(1));
	return string_.join(' ');
};

/**
 * return array with Capitalize first latter
 * @function handleCapitalizeEachIdx
 * @param {array}
 * @returns {array}
 */
export const handleCapitalizeEachIdx = list => list.map(item => handleCapitalize(item));

/**
 * return string Capitalize first letter
 * @function handleCapitalize
 * @param {string}
 * @returns {string}
 */
// return duration from Start and End time
export const handleCapitalize = value => {
	return value && value.toString()
		? value && value.toString().charAt(0).toUpperCase() + value.toString().slice(1)
		: undefined;
};

/**
 * check if every item value is true, else return false
 * @function checkInputValid
 * @param {array of values}
 * @returns {Boolean}
 */
export const checkInputValid = array => {
	return array.every(Boolean);
};

/**
 * get url param and return target key value
 * @function getParamKeyValue
 * @param {object, string}
 * @returns {string}
 */
export const getParamKeyValue = (location, target) => {
	const queryParameters = getQueryParamsFromLocation(location);
	return queryParameters[target];
};

/**
 * parase url parameter
 * @function parseUrlParam
 * @param {urlParam = string}
 * @returns {array of params}
 */
export const parseUrlParam = urlParameter => {
	urlParameter = urlParameter.replaceAll('?', '');
	let listParameter = {};

	function splitEaParameter() {
		return urlParameter.split('&');
	}

	function createKeyValue(list) {
		list = typeof list === 'object' ? list : [list];
		for (const element of list) {
			let item = element.split('=');
			listParameter[item[0]] = item[1];
		}
	}

	if (urlParameter.includes('&')) {
		createKeyValue(splitEaParameter(urlParameter));
	} else {
		createKeyValue(urlParameter);
	}
	return listParameter;
};

/**
 * append param to url
 * @function appendParamToUrl
 * @param {string, object}
 * @returns {}
 */
export const appendParamToUrl = (parameter, currentParameters, navigate) => {
	const incomingParameter = queryString.parse(parameter);
	const updatedParameters = {
		...currentParameters,
		...incomingParameter
	};
	setQueryParamsFromObject(updatedParameters, navigate);
};

/**
 * find target in array of objects, return index of target in Array
 * @function findKeyInArrayOfObj
 * @param {array, integer, string}
 * @returns {integer}
 */
export const findKeyInArrayOfObj = (array, value, target) => {
	let index;
	for (const [index_, element] of array.entries()) {
		if (element[target].toString() === value.toString()) {
			index = index_;
		}
	}
	return index;
};

/**
 * handle route to link and update ActiveLink
 * @function handleRoute
 * @param {link = string, navigate}
 * @returns {}
 */
export const handleRoute = (link, navigate, e) => {
	if (e && e.metaKey) {
		window.open(`${window.location.origin}${link}`, '_blank');
	} else {
		navigate(link);
	}
};

export const mergeSortFn = (target, array, direction) => {
	function merge(left, right) {
		// compare the arrays item by item and return the concatenated result
		let result = [];
		let indexLeft = 0;
		let indexRight = 0;

		while (indexLeft < left.length && indexRight < right.length) {
			// convert to lowercase if string
			let leftSide =
				typeof left[indexLeft][target] === 'string'
					? left[indexLeft][target].toLowerCase()
					: left[indexLeft][target];
			let rightSide =
				typeof right[indexRight][target] === 'string'
					? right[indexRight][target].toLowerCase()
					: right[indexRight][target];

			if (direction) {
				if (leftSide < rightSide) {
					result.push(left[indexLeft]);
					indexLeft++;
				} else {
					result.push(right[indexRight]);
					indexRight++;
				}
			} else {
				if (leftSide > rightSide) {
					result.push(left[indexLeft]);
					indexLeft++;
				} else {
					result.push(right[indexRight]);
					indexRight++;
				}
			}
		}

		return result.concat(left.slice(indexLeft)).concat(right.slice(indexRight));
	}

	function mergeSort(array_) {
		if (array_.length === 1 || array_.length === 0) {
			// return once we hit an array with a single item or if there is nothing in the array
			return array_;
		}
		const middle = Math.floor(array_.length / 2); // get the middle item of the array rounded down
		const left = array_.slice(0, middle); // items on the left side
		const right = array_.slice(middle); // items on the right side
		return merge(mergeSort(left), mergeSort(right));
	}

	return mergeSort(array);
};

// Turf.js helper
/**
 *
 * @param {Array} ex: [[longitude, latitude],[-112.2394578, 33.294537]]
 */
export const getPolygonCenterFeature = array => {
	const features = points(array);
	return center(features);
};

export const bytesToSize = bytes => {
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
	if (bytes === 0) return 'n/a';
	const index = Number.parseInt(Math.floor(Math.log(bytes) / Math.log(1024)), 10);
	if (index === 0) return `${bytes} ${sizes[index]}`;
	return `${(bytes / 1024 ** index).toFixed(2)} ${sizes[index]}`;
};

export const isValidCoordinates = (lat, long) => {
	const latitude = lat >= -90 && lat <= 90 ? true : false; // lat -90, 90
	const longitude = long >= -180 && long <= 180 ? true : false; // long -180, 180
	return [latitude, longitude].every(Boolean);
};

export const getUserViewableManageCategories = ({ userRole, user, accountRegion }) => {
	if (isOwnerOfAccount(user)) return createDefaultManageModulesList();
	const keepInList = ['authorizations'];

	const newList = createDefaultManageModulesList().map(category => {
		const updatedCategoryItems = [];
		category.items.map(item => {
			const itemPerm = userRole?.permissions[item.permission];
			if (itemPerm && itemPerm !== 'none') {
				updatedCategoryItems.push(item);
			} else {
				if (keepInList.includes(item.value) && accountRegion === 'us') {
					updatedCategoryItems.push(item);
				}
			}
			return item;
		});
		return {
			...category,
			items: updatedCategoryItems
		};
	});

	return newList;
};

export const getUserViewableManageRoutes = ({ userRole, user, accountRegion }) => {
	const viewableCategories = getUserViewableManageCategories({ userRole, user, accountRegion });
	const viewableRoutes = viewableCategories.flatMap(category =>
		category.items.map(item => item.value)
	);
	return viewableRoutes;
};

/**
 * @param {Array}
 * @ex: [[-122.48841593538896,37.637899643327465],[-122.494735432945,37.6405604836551],[-122.49473543331719,37.643803262253115],[-122.49073622201307,37.64413100548811],[-122.48841593538896,37.637899643327465]]
 */
export const getPolygonCenter = points => {
	let featureArray = [];

	for (const item of points) {
		featureArray.push(point(item));
	}

	const features = featureCollection(featureArray);
	const c = center(features);
	return c.geometry.coordinates;
};

export const focusElementWithId = id => {
	const element = document.getElementById(id);
	if (element) {
		element.focus();
	}
};

export const clickButtonWithId = id => {
	const element = document.getElementById(id);
	if (element) {
		element.click();
	}
};

export const createDownloadLink = (file, data) => {
	const windowUrl = window.URL.createObjectURL(new Blob([data]));
	const link = document.createElement('a');
	link.href = windowUrl;
	link.setAttribute('download', `${file.name}.${file.extension}`);
	document.body.append(link);
	return link;
};

export const createPreviewWindowUrl = ({ data, type }) => {
	return window.URL.createObjectURL(new Blob([data], { type }));
};

export const userFullNameWithComma = user => {
	if (!user) return '';
	// If user first and last name is single field
	if (user['0'] && user['0'].split(' ').length > 1) {
		return `${user['0'].split(' ').slice(-1)}, ${user['0'].split(' ').slice(0, 1)}`;
	}
	// If user first and last names are multiple fields
	return `${handleCapitalize(user.lastname || user.last_name || user['1'])}, ${handleCapitalize(
		user.firstname || user.first_name || user['0']
	)}`;
};

export const coordIsInsidePolygon = (polygon, coord) => {
	let shape = polygon(polygon);
	let p = point(coord);
	return booleanContains(shape, p);
};

const irregularPlurals = {
	aircraft: 'aircraft',
	battery: 'batteries'
};

export const pluralize = singularLabel => {
	return irregularPlurals[singularLabel] ? irregularPlurals[singularLabel] : `${singularLabel}s`;
};

export const unpluralize = plural => {
	let newLabel = plural;
	const length = plural.length;
	const lastCharIndex = length - 1;
	if (valuesIn(irregularPlurals).includes(plural)) {
		const singular = Object.keys(irregularPlurals).find(key => irregularPlurals[key] === plural);
		return singular;
	} else if (plural.charAt(lastCharIndex) === 's') {
		return newLabel.slice(0, Math.max(0, lastCharIndex));
	} else {
		return plural;
	}
};

const renderVideoItem = url => (
	<video id='imageGalleryVideo' controls preload='auto'>
		<source src={url} />
	</video>
);

export const formatImageList = (list, token) => {
	const updatedList = list.map(item => {
		let object = {
			...item,
			src: `${BASE_JWT_URL}${item.view_url}?jwt=${token}`,
			thumbnail: item.thumbnail_url
				? `${BASE_JWT_URL}${item.thumbnail_url}?jwt=${token}`
				: photoIcon,
			downloadURL: `${BASE_JWT_URL}${item.download_url}?jwt=${token}`,
			original: `${BASE_JWT_URL}${item.view_url}?jwt=${token}`,
			title: `${item.name}.${item.extension}`
		};

		if (object.name.includes('flightlog') && object.extension.toLowerCase() === 'json') {
			const date = unixToUTCConvertor(item.created_at, dateTimeLogicFormat2);
			object.name = `FlightLog-${date}`;
		}

		if (['mp4', 'mov', 'avi', 'mpeg4'].includes(item.extension.toLowerCase())) {
			object.renderItem = () => renderVideoItem(`${BASE_JWT_URL}${item.view_url}?jwt=${token}`);
		}

		return object;
	});
	return updatedList;
};

export const addNewKeyToObject = (object, key, target, state = false) => {
	object[target].map(item => {
		return (item[key] = state);
	});
	return object;
};

export const isPromise = object => {
	if (Promise && Promise.resolve) {
		return Promise.resolve(object) === object;
	} else {
		throw new Error('Promise not supported in your environment');
	}
};

/**
 * return full list after fetch Complete
 * @param {string} url
 * @return array
 */
export const fetchAllPages = url => {
	let list = [];
	const fetchPages = async function (pageNo = 1) {
		const results = await axiosInstance.get(`${url}&page=${pageNo}`);

		if (!results.data || !results.data.paginate) return;
		const last_page = results.data.paginate.last_page;
		list = [...list, ...results.data.data];

		return pageNo < last_page ? await fetchPages(pageNo + 1) : list;
	};
	return fetchPages();
};

/**
 * global pending request Object Varaible
 */
export let manageAbortContollers = {
	abortCurrentRequest: null
};

/**
 * @description
 * if an existing request is active, it will Abort request before making this request.
 * table list request are done asynchronous, so requesting page 2 of a table will only happens when page 1 is finish.
 * @param {fn} abortRequest
 */
export const abortActiveRequest = canceler => {
	if (canceler) {
		canceler();
	}
};

// Split an array (array parameter) into even chunks (e parameter)
export const nthArray = (array, e) => {
	const array_ = [];
	const delta = Math.pow(2, Math.ceil(Math.log2(array.length / e)));
	for (let index = 0; index < array.length; index = index + delta) {
		array_.push(index);
	}
	return array_;
};

export const isJSON = value => {
	try {
		JSON.parse(value);
	} catch {
		return false;
	}
	return true;
};

export const parseJSONObjectKeys = object => {
	let newObject = {};
	for (const key of Object.keys(object)) {
		const value = object[key];
		let newValue = value;
		if (typeof value === 'string' && isJSON(value)) {
			//Get value from JSON string
			newValue = JSON.parse(value);
		}
		newObject[key] = newValue;
	}
	return newObject;
};

export const findObjectDifferentProperties = (object1, object2) => {
	let differingKeys = [];
	for (const key of Object.keys(object1)) {
		if (object1[key] !== object2[key]) {
			differingKeys.push(key);
		}
	}
	for (const key of Object.keys(object2)) {
		if (object2[key] !== object1[key]) {
			differingKeys.push(key);
		}
	}
	return uniq(differingKeys);
};

export const swapPlacesInArray = (array, index1, index2) => {
	const newArray = [...array];
	const temporary = newArray[index1];
	newArray[index1] = newArray[index2];
	newArray[index2] = temporary;
	return newArray;
};

export const arrayMove = (arr, oldIndex, newIndex) => {
	const itemToMove = arr[oldIndex];
	// //Remove at old index
	const removedOldIndex = arr.toSpliced(oldIndex, 1);
	// //Add at new index
	const addToNewIndex = removedOldIndex.toSpliced(newIndex, 0, itemToMove);
	return addToNewIndex;
};

export const interpolateColor = (color1, color2, fraction) => {
	// Parse hex color strings to integers
	let color1RGB = parseInt(color1.slice(1), 16);
	let color2RGB = parseInt(color2.slice(1), 16);

	// Extract RGB components
	let r1 = (color1RGB >> 16) & 0xff;
	let g1 = (color1RGB >> 8) & 0xff;
	let b1 = color1RGB & 0xff;

	let r2 = (color2RGB >> 16) & 0xff;
	let g2 = (color2RGB >> 8) & 0xff;
	let b2 = color2RGB & 0xff;

	// Linearly interpolate each component
	let r = Math.round(r1 + (r2 - r1) * fraction);
	let g = Math.round(g1 + (g2 - g1) * fraction);
	let b = Math.round(b1 + (b2 - b1) * fraction);

	// Construct the interpolated hex color
	return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};
